/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */
// Core imports
import { Fragment } from 'react';
import { Link as NavigationLink, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment';

// Component imports
import { Button, Col, Descriptions, Divider, Grid, Row, Select, Space, Tag, Tooltip, Typography } from 'antd';
import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import DeploymentChannel from '../DeploymentChannel';

// Misc imports
import { BOT_DEPLOYMENTS_CHANNELS, SESSION_ANALYSIS_FEATURES, TAG_SCOPES } from '../../../constants/main';
import { NAV_BOT } from '../../../constants/navigation';
import { ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX } from '../../../constants/regex';
import { INTEGRATIONS, PLUGIN_CATEGORY } from '../../../constants/integrations';
import { useGenerateAISummaryMutation, useUpdateChatSessionMutation } from '../../../store/chatSessions/chatSessionsApi';
import { useCreateTagMutation } from '../../../store/tags/tagsApi';
import { useTranslation } from 'react-i18next';
import { isEmptyObject } from '../../../utils/is/isEmptyObject';
import { truncate } from '../../../utils/string/truncate';
import { useFetchBotQuery } from '../../../store/bots/botsApi';
import { IS_HRWIZ_SESSION_ANALYSIS_DISABLED, IS_HRWIZ_SESSION_DETAILS_DISABLED } from '../../../constants/env';
import replaceUrlParams from '../../../utils/string/replaceUrlParams';

const { Paragraph, Text, Link } = Typography;

// Constant declarations
const { useBreakpoint } = Grid;

const SessionDetailsTab = ({ chatSession, tags, onFetchChatSession, isLoading, activeBotDeployment }) => {
  const { t } = useTranslation();
  const { orgId } = useParams();
  const screens = useBreakpoint();
  const tenantId = chatSession?.tenant?.id;

  const { data: bot } = useFetchBotQuery({ orgId, tenantId }, { skip: !tenantId });
  const [updateChatSession, { isLoading: isUpdatingChatSession }] = useUpdateChatSessionMutation();
  const [createTag, { isLoading: isCreatingTag }] = useCreateTagMutation();
  const [generateAISummary, { isLoading: isGeneratingAISummary }] = useGenerateAISummaryMutation();

  const summarizationPlugin = bot?.plugins?.find(plugin => plugin?.category === PLUGIN_CATEGORY.LLM_SUMMARIZATION && plugin?.settings?.enabled);
  const isPluginEnabled = !!summarizationPlugin;
  const hasAnalysisData = (chatSession?.summary || chatSession?.sentiment || chatSession?.urgency || chatSession?.resolution || (chatSession?.features && !isEmptyObject(chatSession?.features)));

  if (!chatSession) {
    return (
      <Text type="secondary">
        {t('No session data available.')}
      </Text>
    );
  }

  const handleTagChange = async (record, values) => {
    const createdTag = values.find(value => !tags.find(tag => tag.label === value));
    const data = {
      'label': createdTag,
      'scope': TAG_SCOPES.CHAT_SESSIONS
    };

    const isCreateSuccesful = createdTag ? await createTag({ orgId, data }).unwrap() : false;
    const isUpdateSuccesful = await updateChatSession({
      orgId,
      chatSessionId: record.id,
      data: { 'tags': values },
      urlParams: { tenantIds: [tenantId] }
    }).unwrap();

    if (isCreateSuccesful || isUpdateSuccesful) {
      onFetchChatSession(record);
    }
  };

  const renderAISummaryContents = () => {

    if (isPluginEnabled && (chatSession?.summary || chatSession?.sentiment || chatSession?.urgency || chatSession?.resolution || (chatSession?.features && !isEmptyObject(chatSession?.features)))) {
      return (
        <Fragment>
          <Row hidden={!(chatSession?.summary)}>
            <Col xs={24}>
              <Space direction="vertical">
                <Typography.Text strong>
                  {t('Summary')}:
                </Typography.Text>
                <Text
                  className="fs-base"
                  hidden={!isPluginEnabled || !(chatSession?.summary)}>
                  <ReactMarkdown
                    skipHtml
                    linkTarget="_blank"
                  >
                    {chatSession?.summary}
                  </ReactMarkdown>
                </Text>
              </Space>
            </Col>
          </Row>
          <Row
            className="pb-2"
            hidden={!chatSession?.sentiment && !chatSession?.urgency && !chatSession?.resolution}
          >
            <Col span={24}>
              <Row wrap gutter={[16, 16]}>
                <Col hidden={!chatSession.sentiment}>
                  <Space size="small" wrap={false}>
                    <Text strong>{t(`Sentiment`)}:</Text>
                    <Tag
                      className="fs-sm m-0 px-1"
                      {...Object.values(Object.values(SESSION_ANALYSIS_FEATURES.SENTIMENT)).includes(chatSession?.sentiment?.toLowerCase())
                        && { color: chatSession?.sentiment?.toLowerCase() === SESSION_ANALYSIS_FEATURES.SENTIMENT.POSITIVE ? 'success'
                          : chatSession?.sentiment?.toLowerCase() === SESSION_ANALYSIS_FEATURES.SENTIMENT.NEGATIVE ? 'error' : 'warning' }}
                    >
                      {chatSession?.sentiment
                        ? chatSession.sentiment.charAt(0).toUpperCase() + chatSession.sentiment.slice(1)
                        : 'Unknown'
                      }
                    </Tag>
                  </Space>
                </Col>
                <Col hidden={!chatSession.resolution}>
                  <Space wrap={false}>
                    <Text strong>{t(`Resolution`)}:</Text>
                    <Tag
                      className="fs-sm m-0 px-1"
                      {...Object.values(Object.values(SESSION_ANALYSIS_FEATURES.RESOLUTION)).includes(chatSession?.resolution?.toLowerCase())
                         && { color: chatSession?.resolution?.toLowerCase() === SESSION_ANALYSIS_FEATURES.RESOLUTION.RESOLVED ? 'success'
                           : chatSession?.resolution?.toLowerCase() === SESSION_ANALYSIS_FEATURES.RESOLUTION.UNRESOLVED ? 'error' : 'warning' }}
                    >
                      {chatSession?.resolution
                        ? chatSession.resolution.charAt(0).toUpperCase() + chatSession.resolution.slice(1)
                        : 'Unresolved'
                      }
                    </Tag>
                  </Space>
                </Col>
                <Col hidden={!chatSession.urgency}>
                  <Space size="small" wrap={false}>
                    <Text strong>{t(`Urgency`)}:</Text>
                    <Tag
                      className="fs-sm m-0 px-1"
                      {...Object.values(Object.values(SESSION_ANALYSIS_FEATURES.URGENCY)).includes(chatSession?.urgency?.toLowerCase())
                        && { color: chatSession?.urgency?.toLowerCase() === SESSION_ANALYSIS_FEATURES.URGENCY.LOW ? 'success'
                          : chatSession?.urgency?.toLowerCase() === SESSION_ANALYSIS_FEATURES.URGENCY.URGENT ? 'error' : 'warning' }}
                    >
                      {chatSession?.urgency
                        ? chatSession.urgency.charAt(0).toUpperCase() + chatSession.urgency.slice(1)
                        : 'Normal'
                      }
                    </Tag>
                  </Space>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row hidden={!chatSession?.features || isEmptyObject(chatSession?.features)}>
            <Col xs={24}>
              {
                chatSession?.features && !isEmptyObject(chatSession?.features) && Object.entries(chatSession?.features).map(([featureName, featureValue]) => {
                  return <Row key={featureName} wrap gutter={[16, 16]}>
                    <Col xs={8}>
                      <Typography.Text strong>
                        <Tooltip
                          title={featureName?.length >= 17 && featureName}
                        >
                          {truncate(featureName, 17, '...')}:
                        </Tooltip>
                      </Typography.Text>
                    </Col>
                    <Col xs={16}>
                      <Typography.Text>
                        {featureValue?.toString()}
                      </Typography.Text>
                    </Col>
                  </Row>;
                })
              }
            </Col>
          </Row>
          <Row className="d-flex justify-content-center align-items-center text-center">
            <Col xs={24}>
              <Text
                hidden={!isPluginEnabled || (!(chatSession?.summary) && !(chatSession?.sentiment) && !(chatSession?.urgency) && !(chatSession?.resolution) && (!chatSession?.features || isEmptyObject(chatSession?.features)))}
                className="fs-sm w-100">
                {t(`Session Analysis is generated by`)}
                {INTEGRATIONS?.find(integration => summarizationPlugin?.integration?.type === integration?.type)?.renderTypeIcon?.({ className: 'mx-1' })}
                {t(INTEGRATIONS?.find(integration => summarizationPlugin?.integration?.type === integration?.type)?.type, { context: 'type' })}
                <NavigationLink
                  to={replaceUrlParams(NAV_BOT, { tenantId, orgId }) + '?view=plugins&scope=LLM_SUMMARIZATION'}
                  rel="noopener noreferrer"
                  className="ml-1"
                >
                  {`${t('LLM Plugin')}`}
                </NavigationLink>
              </Text>
            </Col>
          </Row>
        </Fragment>
      );
    } else {
      return (
        <Text type="secondary">
          {!isPluginEnabled ? t(`The Session Analysis plugin has not been activated yet. `) : t(`The Session hasn’t been analysed yet. To analyse it, click on 'Run' button.`)}
          {!isPluginEnabled ? t('To activate it, ') : ''}
          <NavigationLink
            hidden={isPluginEnabled}
            to={replaceUrlParams(NAV_BOT, { tenantId, orgId }) + '?view=plugins&scope=LLM_SUMMARIZATION'}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t('go to the Session Analysis plugin section.')}
          </NavigationLink>
        </Text>
      );
    }
  };

  return (
    <div className="overflow-y-auto pl-2 pr-2 overflow-x-hidden" style={{ maxHeight: 590 }}>
      <Text className={'fw-bold fs-lg mb-3 d-block'}>{t('Session details')}</Text>
      <Descriptions
        bordered={true}
        column={1}
        labelStyle={{ fontWeight: 500, backgroundColor: 'inherit' }}
        contentStyle={{ backgroundColor: 'inherit' }}
      >
        {!IS_HRWIZ_SESSION_DETAILS_DISABLED && (
          <Descriptions.Item label={t('Session ID:')}>
            <Text className="pr-6" copyable>{chatSession.id}</Text>
          </Descriptions.Item>
        )}

        {!IS_HRWIZ_SESSION_DETAILS_DISABLED && (
          <Descriptions.Item label={t('Tags:')}>
            <Select
              mode="tags"
              className="w-80"
              tokenSeparators={[',']}
              placeholder={t('Add a tag for the session')}
              loading={isLoading || isCreatingTag || isUpdatingChatSession}
              defaultValue={chatSession.tags}
              options={tags.map((tag) => {
                return {
                  value: tag.label,
                  label: tag.label
                };
              })}
              onInputKeyDown={(e) => {
                if (!ALPHANUMERIC_HYPHEN_UNDERSCORE_REGEX.test(e.key)) {
                  e.preventDefault();
                  return false;
                }
              }}
              onChange={(values) => handleTagChange(chatSession, values)}
              maxTagPlaceholder={(omittedValues) => (
                <Tooltip
                  title={
                    <div className="overflow-y-auto overflow-x-hidden scrollbar-custom" style={{ maxHeight: 280, maxWidth: 220 }}>
                      {
                        omittedValues.map((value, index) => (
                          <Paragraph ellipsis key={index} className="fs-base text-muted mb-0">
                            {value.label}
                            <Button
                              size="small"
                              type="link"
                              className="mb-1"
                              icon={<CloseOutlined className="text-secondary mr-0 fs-sm" />}
                              onClick={() => handleTagChange(chatSession, chatSession.tags.filter((tag) => tag !== value.label))}
                            />
                          </Paragraph>
                        ))
                      }
                    </div>
                  }
                  placement="bottomLeft"
                >
                  +{omittedValues.length}
                </Tooltip>
              )}
            />
          </Descriptions.Item>
        )}

        <Descriptions.Item label={t('Total Messages:')}>
          {`${chatSession.messageCount} ${t('messages')}`}
        </Descriptions.Item>

        {!IS_HRWIZ_SESSION_DETAILS_DISABLED && (
          <Descriptions.Item label={t('Language:')}>
            {chatSession?.language ? chatSession.language
              : <Text className="text-secondary">{t('(Not available)')}</Text>}
          </Descriptions.Item>
        )}

        <Descriptions.Item label={t('Duration:')}>
          {moment.utc(chatSession.duration * 1000).format('HH:mm:ss')}
        </Descriptions.Item>

        <Descriptions.Item label={t('Deployment:')}>
          {
            chatSession.metadata?.deploymentId && chatSession.metadata?.deploymentId ? (
              <DeploymentChannel
                deploymentChannelTitle={chatSession.metadata?.deploymentName}
                {...chatSession.metadata?.deploymentPlatform ? {
                  deploymentChannelIcon: Object.values(BOT_DEPLOYMENTS_CHANNELS).find(
                    platform => platform.key === chatSession.metadata?.deploymentPlatform
                  )?.icon
                } : {}}
              />
            ) : <Text className="text-secondary">{t('(Not available)')}</Text>
          }
        </Descriptions.Item>

        {!IS_HRWIZ_SESSION_DETAILS_DISABLED && (
          <Descriptions.Item ellpisis label={t('Origin:')}>
            {
              chatSession.metadata?.platformOrigin ? (
                <Link style={{ maxWidth: screens.xxl ? 250 : 150 }} className="pr-5" ellipsis href={chatSession.metadata?.platformOrigin} target="_blank">
                  {chatSession.metadata?.platformOrigin}
                </Link>
              ) : (
                <Text className="text-secondary">{t('(Not available)')}</Text>
              )
            }
          </Descriptions.Item>
        )}
      </Descriptions>

      {!IS_HRWIZ_SESSION_ANALYSIS_DISABLED && (
        <>
          <Row align="middle" wrap={false} className="mb-1 mt-1">
            <Col flex="none" className="pr-5">
              <Text className="fw-bold fs-lg">
                {t('Session Analysis')}
                <Tooltip
                  className="ml-1"
                  placement="top"
                  title={t('The Session Analysis provides an AI-generated overview of the session, including a concise summary, three-level ratings for Sentiment, Resolution, and Urgency, along with key session parameters for quick insight.')}
                >
                  <QuestionCircleOutlined className="fs-sm position-absolute" />
                </Tooltip>
              </Text>
            </Col>

            {isPluginEnabled && (
              <Fragment>
                <Col flex="auto">
                  <Divider className="opacity-50"/>
                </Col>

                <Col flex="none" className="pl-3">
                  <Button
                    className="text-color-default"
                    type="ghost"
                    onClick={async () => {
                      if (isGeneratingAISummary) {
                        return;
                      }
                      await generateAISummary({ orgId, tenantId, chatSessionId: chatSession.id });
                    }}
                    disabled={chatSession.status !== 'COMPLETED' || isGeneratingAISummary}
                    loading={isGeneratingAISummary}
                  >
                    {hasAnalysisData ? t('Rerun') : t('Run')}
                  </Button>
                </Col>
              </Fragment>
            )}
          </Row>
          {renderAISummaryContents()}
        </>
      )}

    </div>
  );
};

SessionDetailsTab.propTypes = {
  chatSession: PropTypes.object,
  tags: PropTypes.array,
  onFetchChatSession: PropTypes.func,
  isLoading: PropTypes.bool,
  activeBotDeployment: PropTypes.object
};

export default SessionDetailsTab;
